import { useMemo } from 'react';
import { Box, Button, Container, Grid, SxProps, Theme, Typography } from '@mui/material';
import { addMonths } from 'date-fns';
import { useUploadQuoteFiles } from 'shared/mutations/pdf-upload-mutation';
import { CustomerInfo } from 'new/customer-info';
import { OutOfScopeCustomer } from 'new/out-of-scope-customer';
import { ProductInfo } from 'new/product-info';
import { CustomProductInfo } from 'new/custom-product-info';
import { QuoteExpirationDate } from 'new/quote-expiration-date';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'routing/routes';
import { BRANDS, defaultPdfInfo, discountRecommendationBrands, STATUS } from 'shared/constants';
import { useCreateQuote, useExecuteModel, useUpdateQuote } from 'shared/mutations';
import { useUsers } from 'shared/queries';
import {
  Brands,
  ModelInput,
  ModelOutput,
  IBasicQuoteForm,
  Quote,
  IQuoteDocumentation,
  QuoteWithoutId,
  UserInfo,
} from 'shared/types';
import {
  addPdfExtension,
  buildModelPayload,
  generateQuoteNumber,
  getDemoPCQAuthClient,
  hashFileName,
  isPrePopulateTermsBrand,
  parseModelResult,
} from 'shared/utility';
import { AttachPdf } from './attach-pdf';
import { OutcomeDropdown } from '../shared/components/outcome-dropdown';
import { QuoteReminderDate } from './quote-reminder-date';
import { OrderNotesInfo } from './order-notes-info';

const rootStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 1.25,
  height: '100%',
  width: '100%',
};

interface BasicQuoteFormProps {
  brand: Brands;
  user: UserInfo;
  methods: UseFormReturn<IBasicQuoteForm>;
}
export function BasicQuoteForm({ brand, user, methods }: BasicQuoteFormProps) {
  const isDiscountBasedBrand: boolean = discountRecommendationBrands.includes(brand);
  const isQualityStainless: boolean = brand === 'qualitystainless';
  const isCimtec: boolean = brand === 'cimtec';
  const nowISOString = useMemo(() => new Date().toISOString(), []);
  const history = useHistory();
  const { users } = useUsers(brand, user.id);
  const createMutation = useCreateQuote();
  const executeMutation = useExecuteModel<ModelInput, ModelOutput>('parent');
  const updateMutation = useUpdateQuote();
  const uploadPdfMutation = useUploadQuoteFiles();
  const defaultExpiration = addMonths(new Date(), isQualityStainless ? 3 : 1);
  const { handleSubmit } = methods;

  const toQuoteList = () => {
    history.push(ROUTES.getSite(brand));
  };

  const handleFiles = async (data: IBasicQuoteForm, index: number, user: string, fileDate: string, id: string) => {
    const originalDocumentName = data.files?.[index].name || '';
    const fileId = hashFileName(`${id}${originalDocumentName}${nowISOString}`);
    const formData = new FormData();
    const fileToUpload = data.files ? [data.files[index]] : [];
    formData.append('file', new File(fileToUpload, addPdfExtension(fileId)));

    await uploadPdfMutation.mutateAsync(formData);
  };

  const onSubmit = async (data: IBasicQuoteForm) => {
    try {
      const today = new Date();
      const { customProducts } = data;

      const modelPayload = buildModelPayload(data.customer, data.products, brand);

      const calcResponse = await executeMutation.mutateAsync(modelPayload);

      const {
        customerTier,
        competitionAdjustment,
        competitiveAdjustment,
        products,
        quoteTotal,
        marginFloor,
        calculationId,
      } = parseModelResult(data.products, calcResponse, isDiscountBasedBrand, brand, customProducts);
      const authUser = users?.filter(authUser => authUser.id === user.id)[0];
      const customerNumber = data.customer.customerNumber.toUpperCase();
      const name = data.customer.name.toUpperCase();
      const userEmail = user.email?.toUpperCase() || authUser.email.toUpperCase();
      const userDisplayName = user.displayName.toUpperCase();

      const expenses = data.expenses ? data.expenses : [];
      const orderNotes = data.orderNotes ? data.orderNotes : [];
      orderNotes.forEach(note => {
        note.createdBy = user.displayName.toUpperCase();
        note.creationDate = new Date().toISOString();
      });

      const demoPCQClient = getDemoPCQAuthClient(authUser);
      const quotedByPhoneNumber = demoPCQClient.attributes?.salesRepPhone || '';
      const quoteTermsConsideringBrands =
        brand === 'indelco'
          ? 'For Terms and Conditions, go to Indelco.com/terms-service'
          : brand === 'qualitystainless'
          ? 'Standard Terms and Conditions Apply - Pricing valid for 24 hours'
          : defaultPdfInfo.quoteTerms;

      const newQuote: QuoteWithoutId = {
        quoteNumber: generateQuoteNumber(customerNumber, today),
        expirationDate: data.expirationDate?.toISOString() || '',
        reminderDate: data.reminderDate?.toISOString() || '',
        brand,
        marginFloor,
        salesRep: userDisplayName,
        quoteDocumentation: [],
        salesRepEmail: userEmail,
        assignedSalesRep: data.assignedSalesRep.value,
        ...(isCimtec
          ? {
              salesLocation: data.salesLocation,
            }
          : {}),
        salesRepPhoneNumber: quotedByPhoneNumber,
        status: STATUS.draft,
        outcome: data.outcome,
        createdDate: today.toISOString(),
        lastRevisionDate: today.toISOString(),
        submitDate: '',
        quoteFilesToDelete: [],
        quoteTotal,
        customProducts,
        customerSearch: data.customer.isNew ? `${customerNumber} - ${name}` : data.customerSearch,
        customer: {
          ...data.customer,
          customerTier: customerTier || 'TIER II',
          competitionAdjustment,
          competitiveAdjustment,
          name,
          customerNumber,
          salesLocationId: '100',
        },
        pdfInfo: {
          ...defaultPdfInfo,
          quoteTerms: quoteTermsConsideringBrands,
          quotedBy: userDisplayName,
          quotedByEmail: userEmail,
          quotedByPhoneNumber,
          ...(isPrePopulateTermsBrand(brand)
            ? {
                paymentTerms: data.customer.paymentTerms || 'NET 30',
                salesRep: data.customer.assignedSalesReps || '',
                salesRepEmail: data.customer.assignedSalesRepsEmail || '',
              }
            : {}),
        },
        products,
        expenses,
        orderNotes,
        fees: [],
        calculationId,
      };
      newQuote.reminderDate = newQuote.reminderDate === '' ? undefined : newQuote.reminderDate;
      const created = await createMutation.mutateAsync(newQuote);

      let additionalDocumentation: IQuoteDocumentation[] = [];
      if (data?.files) {
        for (let i = 0; i < data?.files?.length; i++) {
          handleFiles(data, i, name, nowISOString, created.id);
          additionalDocumentation.push({
            id: hashFileName(`${created.id}${data.files?.[i].name || ''}${nowISOString}`),
            documentName: data.files?.[i].name || '',
            user: name,
            uploadDate: nowISOString,
          });
        }
      }

      const addedFilesQuote: Quote = {
        ...created,
        quoteDocumentation: additionalDocumentation,
      };

      await updateMutation.mutateAsync(addedFilesQuote);
      history.push(ROUTES.toQuote(created.brand, created.id));
    } catch (error) {
      console.error(error);
    }
  };

  const buttonText = executeMutation.isLoading
    ? 'Calculating Prices...'
    : createMutation.isLoading
    ? 'Creating Quote...'
    : 'Create New Quote';

  const disableButton = executeMutation.isLoading || createMutation.isLoading;
  return (
    <FormProvider {...methods}>
      <Box component='form' aria-label='form' sx={rootStyles} onSubmit={handleSubmit(onSubmit)}>
        <Typography variant='h5'>New Quote for {BRANDS[brand]}</Typography>
        <Container>
          <Grid container spacing={2} direction='column'>
            <Grid item>
              <QuoteExpirationDate initialDate={defaultExpiration} />
            </Grid>
            <Grid item>
              <QuoteReminderDate />
            </Grid>
            <Grid item>
              <OutcomeDropdown />
            </Grid>
            <Grid item>
              <CustomerInfo loggedInUserId={user.id} brand={brand} />
            </Grid>
            <Grid item>
              <ProductInfo />
            </Grid>
            <Grid item>
              <CustomProductInfo />
            </Grid>
            <Grid item>
              <AttachPdf />
            </Grid>
            <Grid item>
              <OrderNotesInfo />
            </Grid>
            <Grid item container justifyContent='flex-end' gap={3}>
              <Grid item>
                <Button onClick={toQuoteList} variant='outlined'>
                  Go Back
                </Button>
              </Grid>
              <Grid item>
                <Button type='submit' variant='contained' disabled={disableButton}>
                  {buttonText}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <OutOfScopeCustomer />
    </FormProvider>
  );
}
